import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { catalog, type CatalogItem } from "../lib/api";
import { money } from "../lib/utils";
import { useActiveBranch } from "../lib/session";
import { ProductForm, type ProductFormInit } from "../components/ProductForm";
import { QueryError } from "../components/QueryError";

// Catálogo: consume catalog_list (RPC real). Búsqueda full-text server-side.
// Permite crear/editar producto (form multi-paso).
export const Route = createFileRoute("/catalogo")({ component: Catalogo });

const PAGE = 50;

function Catalogo() {
  const branch = useActiveBranch();
  const [search, setSearch] = useState("");
  const [form, setForm] = useState<ProductFormInit | null>(null); // null = cerrado
  const { data: pages, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["catalog", branch, search],
    queryFn: ({ pageParam }) => catalog.list(branch, search || undefined, PAGE, pageParam),
    initialPageParam: 0,
    // Hay más páginas si la última llenó el límite; el offset siguiente acumula.
    getNextPageParam: (last, all) => (last.length === PAGE ? all.length * PAGE : undefined),
  });
  const data = pages?.pages.flat() ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Catálogo</h1>
        <button onClick={() => setForm({})} className="btn btn-primary">+ Nuevo producto</button>
      </div>
      <QueryError error={error} />
      <input
        value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="🔍 Buscar por nombre o SKU…"
        className="mb-4 w-full max-w-md rounded-lg border px-4 py-2 outline-none focus:border-brand"
      />
      <div className="overflow-hidden rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-grafito-50 text-left text-grafito-400">
            <tr><th className="px-4 py-2">SKU</th><th className="px-4 py-2">Producto</th><th className="px-4 py-2 text-right">Disponible</th><th className="px-4 py-2 text-right">Precio</th></tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-grafito-400">Cargando…</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-grafito-400">Sin resultados.</td></tr>
            ) : data.map((p: CatalogItem) => (
              <tr key={p.id} className="cursor-pointer border-t hover:bg-grafito-50"
                onClick={() => setForm({ id: p.id, sku: p.sku, name: p.name, itbmsRate: p.itbms_rate })}>
                <td className="px-4 py-2 font-mono text-xs">{p.sku}</td>
                <td className="px-4 py-2">{p.name}</td>
                <td className="px-4 py-2 text-right">{p.qty_available}</td>
                <td className="px-4 py-2 text-right">{p.units[0]?.price != null ? money(p.units[0].price) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasNextPage && (
        <div className="mt-3 text-center">
          <button onClick={() => fetchNextPage()} disabled={isFetchingNextPage} className="btn btn-ghost text-sm disabled:opacity-40">
            {isFetchingNextPage ? "Cargando…" : "Cargar más"}
          </button>
        </div>
      )}

      {form && <ProductForm init={form} onDone={() => setForm(null)} onCancel={() => setForm(null)} />}
    </div>
  );
}
